/**
 * Frelan trade cart drawer (snippets/trade-cart.liquid): a list of every line and a panel
 * with one selected line's detail.
 *
 * Keeps which line is selected and, below 1100px, which panel is showing (list or detail).
 * Both live on attributes the cart re-render leaves alone: this element and each line panel
 * carry `data-skip-node-update`, so the morph updates their contents but not their
 * `data-view` / `hidden` / `aria-current`. A MutationObserver re-applies the selection when
 * lines are added or removed.
 *
 * A plain HTMLElement on purpose: Horizon treats a `*-component` tag as a ref boundary, and
 * the line refs (cartItemRows, quantity selectors, errors) must stay with the
 * cart-items-component around this element.
 */

const WIDE = window.matchMedia('(min-width: 1100px)');

class TradeCart extends HTMLElement {
  /** @type {string | null} */
  #selectedKey = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  #applyQueued = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#observer = new MutationObserver(this.#queueApply);
    this.#observer.observe(this, { childList: true, subtree: true });
    this.#apply();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
  }

  get #panels() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.trade-cart__line')));
  }

  get #rows() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.trade-cart__row')));
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Removing a line: move the selection to the next line (or the previous one, for the
    // last line) now, so the panel that comes up when it is gone is its neighbour.
    const remove = target.closest('.cart-items__remove');
    if (remove) {
      const panel = remove.closest('.trade-cart__line');
      if (panel instanceof HTMLElement) this.#selectNeighbour(panel);
      return;
    }

    const back = target.closest('[data-trade-cart-back]');
    if (back) {
      this.#showList();
      return;
    }

    const select = target.closest('[data-trade-cart-select]');
    if (select instanceof HTMLElement && select.dataset.lineKey) {
      const fromList = select.classList.contains('trade-cart__row');
      this.select(select.dataset.lineKey, { openDetail: fromList });
    }
  };

  /**
   * Selects a line. From the list on a narrow screen it also opens the detail panel.
   * @param {string} key - The line item key
   * @param {{ openDetail?: boolean }} [options]
   */
  select(key, { openDetail = false } = {}) {
    this.#selectedKey = key;
    if (openDetail && !WIDE.matches) this.dataset.view = 'detail';
    this.#apply();

    const panel = this.#panelFor(key);
    panel?.closest('.trade-cart__detail')?.scrollTo({ top: 0 });
    if (openDetail && !WIDE.matches) {
      /** @type {HTMLElement | null | undefined} */ (panel?.querySelector('.trade-cart__line-title'))?.focus({
        preventScroll: true,
      });
    }
  }

  /** @param {HTMLElement} panel - The line being removed */
  #selectNeighbour(panel) {
    const panels = this.#panels.filter((p) => p !== panel && !p.classList.contains('removing'));
    if (!panels.length) return;

    const index = this.#panels.indexOf(panel);
    const next = this.#panels.slice(index + 1).find((p) => panels.includes(p));
    const previous = this.#panels.slice(0, index).reverse().find((p) => panels.includes(p));
    this.#selectedKey = (next ?? previous)?.dataset.lineKey ?? null;
  }

  #showList() {
    this.dataset.view = 'list';
    const row = this.#rows.find((r) => r.dataset.lineKey === this.#selectedKey);
    row?.focus({ preventScroll: false });
  }

  /** @param {string} key */
  #panelFor(key) {
    return this.#panels.find((panel) => panel.dataset.lineKey === key) ?? null;
  }

  #queueApply = () => {
    if (this.#applyQueued) return;
    this.#applyQueued = true;
    queueMicrotask(() => {
      this.#applyQueued = false;
      this.#apply();
    });
  };

  /** Shows the selected line's panel (the first line when the selected one is gone). */
  #apply() {
    const panels = this.#panels.filter((panel) => !panel.classList.contains('removing'));
    if (!panels.length) return;

    if (!panels.some((panel) => panel.dataset.lineKey === this.#selectedKey)) {
      this.#selectedKey = panels[0]?.dataset.lineKey ?? null;
    }

    for (const panel of this.#panels) {
      const selected = panel.dataset.lineKey === this.#selectedKey;
      if (panel.hidden === selected) panel.hidden = !selected;
    }

    for (const row of this.#rows) {
      const current = row.dataset.lineKey === this.#selectedKey ? 'true' : 'false';
      if (row.getAttribute('aria-current') !== current) row.setAttribute('aria-current', current);
    }
  }
}

if (!customElements.get('trade-cart')) {
  customElements.define('trade-cart', TradeCart);
}
